import { useCallback } from 'react'
import {
    RiCheckboxBlankLine,
    RiCircleLine,
    RiSubtractLine,
    RiStarLine,
    RiTriangleLine,
} from 'react-icons/ri'
import { getCanvasInstance } from '@/lib/canvasInstance'
import { useEditorStore } from '@/stores/useEditorStore'
import styles from './ShapePanel.module.css'

const shapes = [
    { id: 'rect', icon: RiCheckboxBlankLine, label: 'Rectangulo' },
    { id: 'circle', icon: RiCircleLine, label: 'Circulo' },
    { id: 'line', icon: RiSubtractLine, label: 'Linea' },
    { id: 'triangle', icon: RiTriangleLine, label: 'Triangulo' },
    { id: 'star', icon: RiStarLine, label: 'Estrella' },
]

export function ShapePanel() {
    const { pushUndo } = useEditorStore()

    const addShape = useCallback(async (shapeId: string) => {
        const canvas = getCanvasInstance()
        if (!canvas) return

        const fabric = await import('fabric')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let obj: any

        const baseProps = {
            left: 100,
            top: 100,
            fill: '#add9d1',
            stroke: '#4ea89a',
            strokeWidth: 1,
        }

        switch (shapeId) {
            case 'rect':
                obj = new fabric.Rect({ ...baseProps, width: 150, height: 100, rx: 8, ry: 8 })
                break
            case 'circle':
                obj = new fabric.Circle({ ...baseProps, radius: 60 })
                break
            case 'line':
                obj = new fabric.Line([50, 50, 250, 50], {
                    left: 100,
                    top: 100,
                    stroke: '#4ea89a',
                    strokeWidth: 3,
                })
                break
            case 'triangle':
                obj = new fabric.Triangle({ ...baseProps, width: 120, height: 120 })
                break
            case 'star': {
                const points = createStarPoints(5, 60, 30)
                obj = new fabric.Polygon(points, { ...baseProps })
                break
            }
        }

        if (obj) {
            canvas.add(obj)
            canvas.setActiveObject(obj)
            canvas.requestRenderAll()
            const json = JSON.stringify(canvas.toJSON())
            pushUndo(json)
        }
    }, [pushUndo])

    const handleDragStart = useCallback((e: React.DragEvent, shapeId: string) => {
        e.dataTransfer.setData('application/x-editor-element', JSON.stringify({
            type: 'shape',
            config: { shapeId },
        }))
        e.dataTransfer.effectAllowed = 'copy'
    }, [])

    return (
        <div className={styles.panel}>
            <h3 className={styles.title}>Formas</h3>
            <div className={styles.grid}>
                {shapes.map((shape) => (
                    <button
                        key={shape.id}
                        className={styles.shapeButton}
                        title={shape.label}
                        onClick={() => addShape(shape.id)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, shape.id)}
                    >
                        <shape.icon />
                        <span className={styles.shapeLabel}>{shape.label}</span>
                    </button>
                ))}
            </div>
        </div>
    )
}

function createStarPoints(spikes: number, outerRadius: number, innerRadius: number) {
    const points = []
    const cx = outerRadius
    const cy = outerRadius
    let rot = (Math.PI / 2) * 3

    const step = Math.PI / spikes

    for (let i = 0; i < spikes; i++) {
        points.push({ x: cx + Math.cos(rot) * outerRadius, y: cy + Math.sin(rot) * outerRadius })
        rot += step
        points.push({ x: cx + Math.cos(rot) * innerRadius, y: cy + Math.sin(rot) * innerRadius })
        rot += step
    }
    return points
}
